import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db } from '../firebase/config';
import {
  doc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  addDoc
} from 'firebase/firestore';
import { signOut } from 'firebase/auth';
import DashboardShell from './DashboardShell';
import bestLogo from '../assets/logo-best.jpeg';
import './StudentDashboard.css';

const FOCUS_SUBJECTS = ['BM', 'BI', 'MATH', 'SEJARAH'];
const GRADE_ORDER = ['A', 'B', 'C', 'D', 'E', 'F'];
const sanitizePhoneNumber = (value) => String(value || '').replace(/[^\d]/g, '');
const SUBJECT_ALIASES = {
  SEJ: 'SEJARAH',
  SEJARAH: 'SEJARAH',
  MT: 'MATH',
  MATEMATIK: 'MATH',
};

const StudentDashboard = () => {
  const navigate = useNavigate();

  const [userData, setUserData] = useState(null);

  const [schedules, setSchedules] = useState([]);
  const [upcoming, setUpcoming] = useState([]);

  const [attendanceDocs, setAttendanceDocs] = useState([]);
  const [attendanceLoading, setAttendanceLoading] = useState(true);

  const [todayMood, setTodayMood] = useState(null);
  const [moodLoading, setMoodLoading] = useState(true);
  const [performanceRecords, setPerformanceRecords] = useState([]);
  const [performanceLoading, setPerformanceLoading] = useState(true);

  const [loading, setLoading] = useState(true);
  const [loadingSchedules, setLoadingSchedules] = useState(true);

  const [activeTab, setActiveTab] = useState('home'); // home | schedule | profile | mood | performance
  const [toast, setToast] = useState({ show: false, type: 'ok', msg: '' });

  // Profile edit
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({ fullName: '', phone: '', grade: '' });
  const [savingProfile, setSavingProfile] = useState(false);

  // Mood form
  const moodOptions = [
    { key: 'happy', label: 'Gembira' },
    { key: 'okay', label: 'Okay' },
    { key: 'tired', label: 'Letih' },
    { key: 'sad', label: 'Sedih' },
    { key: 'stressed', label: 'Stress' },
  ];
  const [moodKey, setMoodKey] = useState('');
  const [moodNote, setMoodNote] = useState('');
  const [savingMood, setSavingMood] = useState(false);

  // ==========
  // Helpers
  // ==========
  const showToast = (type, msg) => {
    setToast({ show: true, type, msg });
    setTimeout(() => setToast({ show: false, type: 'ok', msg: '' }), 2600);
  };

  const todayISO = useMemo(() => new Date().toISOString().slice(0, 10), []);

  const toISODateString = (dateValue) => {
    if (!dateValue) return '';
    if (typeof dateValue === 'string') return dateValue;

    // Firestore Timestamp
    if (typeof dateValue === 'object' && typeof dateValue.toDate === 'function') {
      return dateValue.toDate().toISOString().slice(0, 10);
    }

    if (dateValue instanceof Date) return dateValue.toISOString().slice(0, 10);
    return '';
  };

  const formatMalayDate = (isoDate) => {
    if (!isoDate) return '';
    const d = new Date(isoDate + 'T00:00:00');
    const days = ['Ahad', 'Isnin', 'Selasa', 'Rabu', 'Khamis', 'Jumaat', 'Sabtu'];
    const months = ['Jan', 'Feb', 'Mac', 'Apr', 'Mei', 'Jun', 'Jul', 'Ogos', 'Sep', 'Okt', 'Nov', 'Dis'];
    return `${days[d.getDay()]}, ${d.getDate()} ${months[d.getMonth()]} ${d.getFullYear()}`;
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    const [hh, mm] = timeStr.split(':');
    const h = Number(hh);
    if (Number.isNaN(h)) return timeStr;
    const ampm = h >= 12 ? 'PM' : 'AM';
    const displayH = h % 12 === 0 ? 12 : h % 12;
    return `${displayH}:${mm} ${ampm}`;
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Selamat pagi';
    if (hour < 17) return 'Selamat tengahari';
    return 'Selamat petang';
  };

  const isPastSession = (isoDate, startTime) => {
    if (!isoDate || !startTime) return false;
    const now = new Date();
    const session = new Date(isoDate + 'T00:00:00');
    const [hh, mm] = startTime.split(':');
    session.setHours(Number(hh || 0), Number(mm || 0), 0, 0);
    return session < now;
  };

  const safeText = (v) => (v ? String(v) : '-');

  const firstName = useMemo(() => {
    const name = userData?.fullName || '';
    return name.trim().split(' ')[0] || 'Awak';
  }, [userData]);

  const supportNumber = useMemo(
    () => sanitizePhoneNumber(process.env.REACT_APP_SUPPORT_WHATSAPP_NUMBER),
    []
  );

  const buildSupportUrl = useCallback((message) => {
    if (!supportNumber) return '';
    return `https://wa.me/${supportNumber}?text=${encodeURIComponent(message)}`;
  }, [supportNumber]);

  const studentSupportContext = useMemo(() => {
    const name = userData?.fullName || 'pelajar';
    const school = userData?.schoolName || 'sekolah saya';
    const terminal = userData?.terminal || 'terminal saya';
    return { name, school, terminal };
  }, [userData]);

  const generalSupportUrl = useMemo(() => buildSupportUrl(
    `Assalamualaikum BTOP, saya ${studentSupportContext.name} dari ${studentSupportContext.school}. Saya perlukan bantuan berkenaan portal BEST 2.0.`
  ), [buildSupportUrl, studentSupportContext]);

  const moodSupportUrl = useMemo(() => buildSupportUrl(
    `Assalamualaikum BTOP, saya ${studentSupportContext.name} dari ${studentSupportContext.school}. Saya ingin bercakap dengan pihak BTOP tentang keadaan saya hari ini.`
  ), [buildSupportUrl, studentSupportContext]);

  const attendanceSupportUrl = useMemo(() => buildSupportUrl(
    `Assalamualaikum BTOP, saya ${studentSupportContext.name} dari ${studentSupportContext.school} (${studentSupportContext.terminal}). Saya ingin semak atau maklumkan sebab ketidakhadiran saya.`
  ), [buildSupportUrl, studentSupportContext]);

  const joinedLabel = useMemo(() => {
    const createdAt = userData?.createdAt;
    if (!createdAt) return '-';
    try {
      const d = (typeof createdAt?.toDate === 'function') ? createdAt.toDate() : new Date(createdAt);
      return d.toLocaleDateString('ms-MY', { year: 'numeric', month: 'long', day: 'numeric' });
    } catch {
      return '-';
    }
  }, [userData]);

  const formatDateTime = (value) => {
    if (!value) return '';

    try {
      const date = typeof value?.toDate === 'function' ? value.toDate() : new Date(value);
      if (Number.isNaN(date.getTime())) return '';

      return date.toLocaleString('ms-MY', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      });
    } catch {
      return '';
    }
  };

  const moodCheckedInLabel = useMemo(() => {
    const checkInTime = todayMood?.updatedAt || todayMood?.createdAt || todayMood?.checkedInAtLocal;
    return formatDateTime(checkInTime);
  }, [todayMood]);

  const normalizeSubjectKey = (subject) => {
    const key = String(subject || '').trim().toUpperCase();
    return SUBJECT_ALIASES[key] || key;
  };

  const normalizeGrade = (grade) => {
    const key = String(grade || '').trim().toUpperCase();
    return GRADE_ORDER.includes(key) ? key : '-';
  };

  const toNullableNumber = (value) => {
    if (value === null || value === undefined || value === '') return null;
    const number = Number(value);
    return Number.isFinite(number) ? number : null;
  };

  const performanceBySubject = useMemo(() => {
    const recordMap = new Map(
      performanceRecords.map((record) => [normalizeSubjectKey(record.subject), record])
    );

    return FOCUS_SUBJECTS.map((subject) => {
      const record = recordMap.get(subject) || {};
      const hasRecord = Boolean(record.id || record.studentId || record.baselineGrade || record.currentGrade);
      const baselineGrade = normalizeGrade(record.baselineGrade);
      const currentGrade = normalizeGrade(record.currentGrade);
      const baselineIndex = GRADE_ORDER.indexOf(baselineGrade);
      const currentIndex = GRADE_ORDER.indexOf(currentGrade);
      const targetIndex = baselineIndex >= 0 ? Math.max(0, baselineIndex - 2) : -1;
      const targetGrade = targetIndex >= 0 ? GRADE_ORDER[targetIndex] : '-';
      const achieved = currentIndex >= 0 && targetIndex >= 0 && currentIndex <= targetIndex;
      const baselineMark = toNullableNumber(record.baselineMark);
      const currentMark = toNullableNumber(record.currentMark);
      const markProgress = currentMark === null ? 0 : Math.max(0, Math.min(100, currentMark));

      return {
        subject,
        ...record,
        hasRecord,
        baselineGrade,
        baselineMark,
        currentGrade,
        currentMark,
        targetGrade,
        achieved,
        markProgress,
        markDelta: baselineMark === null || currentMark === null ? null : currentMark - baselineMark,
      };
    });
  }, [performanceRecords]);

  const achievedSubjectCount = useMemo(() => {
    return performanceBySubject.filter((item) => item.achieved).length;
  }, [performanceBySubject]);

  // ==========
  // Load user
  // ==========
  useEffect(() => {
    const run = async () => {
      try {
        const user = auth.currentUser;
        if (!user) {
          navigate('/login');
          return;
        }

        const snap = await getDoc(doc(db, 'users', user.uid));
        if (snap.exists()) {
          const data = { uid: user.uid, ...snap.data() };
          setUserData(data);

          // init profile form
          setProfileForm({
            fullName: data.fullName || '',
            phone: data.phone || '',
            grade: data.grade || '',
          });
        }
      } catch (e) {
        console.error('fetchUserData error:', e);
        showToast('bad', 'Ralat load user.');
      } finally {
        setLoading(false);
      }
    };

    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ==========
  // Fetch schedules (location-based)
  // ==========
  useEffect(() => {
    const fetchSchedules = async () => {
      if (!userData?.daerah || !userData?.terminal) return;

      try {
        setLoadingSchedules(true);

        const schedulesRef = collection(db, 'schedules');
        const q = query(
          schedulesRef,
          where('daerah', '==', userData.daerah),
          where('terminal', '==', userData.terminal),
          where('active', '==', true)
        );

        const qs = await getDocs(q);
        const list = [];

        qs.forEach((d) => {
          const data = d.data();
          const iso = toISODateString(data.date);
          list.push({ id: d.id, ...data, dateISO: iso });
        });

        list.sort((a, b) => (a.dateISO || '').localeCompare(b.dateISO || ''));
        setSchedules(list);

        const up = list.filter((s) => s.dateISO && s.dateISO >= todayISO).slice(0, 5);
        setUpcoming(up);
      } catch (e) {
        console.error('fetchSchedules error:', e);
        showToast('bad', 'Ralat load jadual.');
      } finally {
        setLoadingSchedules(false);
      }
    };

    fetchSchedules();
  }, [userData, todayISO]);

  // ==========
  // Fetch attendance (real)
  // ==========
  useEffect(() => {
    const fetchAttendance = async () => {
      if (!userData?.uid) return;

      try {
        setAttendanceLoading(true);

        const ref = collection(db, 'attendance');
        const q = query(ref, where('studentId', '==', userData.uid));
        const qs = await getDocs(q);

        const list = [];
        qs.forEach((d) => list.push({ id: d.id, ...d.data() }));
        setAttendanceDocs(list);
      } catch (e) {
        console.error('fetchAttendance error:', e);
        showToast('bad', 'Ralat muatkan kehadiran.');
      } finally {
        setAttendanceLoading(false);
      }
    };

    fetchAttendance();
  }, [userData]);

  // ==========
  // Fetch mood today (real)
  // ==========
  useEffect(() => {
    const fetchMoodToday = async () => {
      if (!userData?.uid) return;

      try {
        setMoodLoading(true);

        const ref = collection(db, 'mood_checkins');
        const q = query(
          ref,
          where('studentId', '==', userData.uid),
          where('dateISO', '==', todayISO)
        );

        const qs = await getDocs(q);
        if (!qs.empty) {
          // take first doc (1 check-in per day recommended)
          const m = qs.docs[0].data();
          setTodayMood(m);
          setMoodKey(m.moodKey || '');
          setMoodNote(m.note || '');
        } else {
          setTodayMood(null);
          setMoodKey('');
          setMoodNote('');
        }
      } catch (e) {
        console.error('fetchMoodToday error:', e);
      } finally {
        setMoodLoading(false);
      }
    };

    fetchMoodToday();
  }, [userData, todayISO]);

  useEffect(() => {
    const fetchPerformance = async () => {
      if (!userData?.uid) {
        setPerformanceRecords([]);
        setPerformanceLoading(false);
        return;
      }

      try {
        setPerformanceLoading(true);
        const ref = collection(db, 'student_performance');
        const q = query(ref, where('studentId', '==', userData.uid));
        const qs = await getDocs(q);
        const list = qs.docs.map((item) => ({
          id: item.id,
          ...item.data()
        }));

        setPerformanceRecords(list);
      } catch (error) {
        console.error('fetchPerformance error:', error);
        if (error?.code === 'permission-denied') {
          showToast('bad', 'Ralat prestasi: deploy Firestore rules dulu.');
        } else {
          showToast('bad', `Ralat muatkan prestasi${error?.code ? `: ${error.code}` : ''}.`);
        }
      } finally {
        setPerformanceLoading(false);
      }
    };

    fetchPerformance();
  }, [userData]);

  // ==========
  // Attendance computed stats
  // ==========
  const attendanceSet = useMemo(() => {
    const s = new Set();
    attendanceDocs.forEach((a) => {
      const key = `${a.scheduleId || ''}__${a.date || ''}`;
      s.add(key);
    });
    return s;
  }, [attendanceDocs]);

  const pastSchedules = useMemo(() => {
    return schedules.filter((s) => s.dateISO && s.startTime && isPastSession(s.dateISO, s.startTime));
  }, [schedules]);

  const attendedPastCount = useMemo(() => {
    let count = 0;
    pastSchedules.forEach((s) => {
      const key = `${s.id}__${s.dateISO}`;
      if (attendanceSet.has(key)) count += 1;
    });
    return count;
  }, [pastSchedules, attendanceSet]);

  const attendanceRate = useMemo(() => {
    if (!pastSchedules.length) return null;
    return Math.round((attendedPastCount / pastSchedules.length) * 100);
  }, [attendedPastCount, pastSchedules.length]);

  const attendanceTone = useMemo(() => {
    if (attendanceRate === null) return 'pending';
    if (attendanceRate >= 90) return 'good';
    if (attendanceRate >= 75) return 'warn';
    return 'bad';
  }, [attendanceRate]);

  const needsAttendanceFollowUp = useMemo(() => (
    !attendanceLoading
    && pastSchedules.length > 0
    && attendanceRate !== null
    && attendanceRate < 90
  ), [attendanceLoading, attendanceRate, pastSchedules.length]);

  const currentStreak = useMemo(() => {
    // basic: count consecutive days back from today where at least 1 attendance exists
    const dates = new Set();
    attendanceDocs.forEach((a) => {
      if (a.date) dates.add(a.date);
    });

    let streak = 0;
    for (let i = 0; i < 365; i++) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const iso = d.toISOString().slice(0, 10);
      if (dates.has(iso)) streak += 1;
      else break;
    }
    return streak;
  }, [attendanceDocs]);

  // ==========
  // Actions
  // ==========
  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch (e) {
      console.error('logout error:', e);
      showToast('bad', 'Logout gagal.');
    }
  };

  const goSchedule = () => {
    navigate('/dashboard/student/schedule');
  };

  const studentNavItems = [
    { key: 'home', label: 'Utama', onClick: () => setActiveTab('home') },
    { key: 'schedule', label: 'Jadual', onClick: goSchedule },
    { key: 'performance', label: 'Prestasi', onClick: () => setActiveTab('performance') },
    { key: 'mood', label: 'Mood', onClick: () => setActiveTab('mood') },
    { key: 'profile', label: 'Profil', onClick: () => setActiveTab('profile') },
  ];

  // ==========
  // Save mood
  // ==========
  const handleSaveMood = async () => {
    if (!userData?.uid) return;
    if (!moodKey) {
      showToast('bad', 'Pilih mood dulu.');
      return;
    }

    try {
      setSavingMood(true);

      const opt = moodOptions.find((m) => m.key === moodKey);
      const payload = {
        studentId: userData.uid,
        studentName: userData.fullName || '',
        studentEmail: userData.email || '',
        moodKey,
        moodLabel: opt?.label || moodKey,
        emoji: opt?.label || 'Mood',
        note: moodNote || '',
        dateISO: todayISO,
        checkedInAtLocal: new Date().toISOString(),
        createdAt: serverTimestamp(),
      };

      // recommended: 1 per day
      const ref = collection(db, 'mood_checkins');
      const q = query(ref, where('studentId', '==', userData.uid), where('dateISO', '==', todayISO));
      const qs = await getDocs(q);

      if (!qs.empty) {
        // update first doc
        const existingId = qs.docs[0].id;
        await updateDoc(doc(db, 'mood_checkins', existingId), {
          moodKey: payload.moodKey,
          moodLabel: payload.moodLabel,
          emoji: payload.emoji,
          note: payload.note,
          checkedInAtLocal: payload.checkedInAtLocal,
          updatedAt: serverTimestamp(),
        });
      } else {
        await addDoc(ref, payload);
      }

      setTodayMood(payload);
      showToast('ok', 'Mood berjaya disimpan.');
      setActiveTab('home');
    } catch (e) {
      console.error('saveMood error:', e);
      showToast('bad', 'Gagal simpan mood.');
    } finally {
      setSavingMood(false);
    }
  };

  // ==========
  // Save profile
  // ==========
  const validatePhoneMY = (phone) => {
    const cleaned = (phone || '').replace(/\s/g, '');
    const phoneRegex = /^01[0-9]-?[0-9]{7,8}$/;
    return phoneRegex.test(cleaned);
  };

  const handleSaveProfile = async () => {
    if (!userData?.uid) return;
    if (!profileForm.fullName?.trim()) {
      showToast('bad', 'Nama penuh tak boleh kosong.');
      return;
    }
    if (!validatePhoneMY(profileForm.phone)) {
      showToast('bad', 'Telefon tak sah (contoh: 012-3456789)');
      return;
    }

    try {
      setSavingProfile(true);

      const payload = {
        fullName: profileForm.fullName.trim(),
        phone: profileForm.phone.trim(),
        grade: profileForm.grade || '',
        updatedAt: serverTimestamp(),
      };

      await updateDoc(doc(db, 'users', userData.uid), payload);

      setUserData((prev) => ({ ...prev, ...payload }));
      setEditMode(false);
      showToast('ok', 'Profil berjaya dikemaskini.');
    } catch (e) {
      console.error('saveProfile error:', e);
      showToast('bad', 'Gagal kemaskini profil.');
    } finally {
      setSavingProfile(false);
    }
  };

  // ==========
  // UI
  // ==========
  if (loading) {
    return (
      <div className="ios-screen">
        <div className="ios-loader">
          <div className="ios-spinner" />
          <p>Memuatkan papan pemuka...</p>
        </div>
      </div>
    );
  }

  return (
    <DashboardShell
      brand="BTOP Wellness"
      subtitle="Papan Pemuka Pelajar"
      roleLabel="Pelajar"
      userName={userData?.fullName}
      userInitial={userData?.fullName?.charAt(0)?.toUpperCase() || 'S'}
      navItems={studentNavItems}
      activeKey={activeTab === 'schedule' ? 'home' : activeTab}
      onLogout={handleLogout}
      tone="blue"
    >
    <div className="ios-screen">
      {/* Toast */}
      {toast.show && (
        <div className={`ios-toast ${toast.type}`}>
          <span className={`ico toast-ico ${toast.type === 'ok' ? 'toast-ico-ok' : 'toast-ico-bad'}`} aria-hidden="true" />
          <span>{toast.msg}</span>
        </div>
      )}

      {/* Top Bar */}
      <header className="ios-topbar">
        <div className="ios-topbar-left">
          <div className="ios-appicon">
            <img src={bestLogo} alt="BEST BTOP logo" />
          </div>
          <div className="ios-titles">
            <div className="ios-title">BTOP Wellness</div>
            <div className="ios-subtitle">Pelajar</div>
          </div>
        </div>

        <div className="ios-topbar-right">
          <button className="ios-iconbtn" type="button" aria-label="Notifikasi">
            <span className="ios-icon ios-icon-bell" aria-hidden="true" />
            <span className="ios-badge">3</span>
          </button>

          <button
            className="ios-avatar"
            type="button"
            onClick={() => setActiveTab('profile')}
            title="Profil"
          >
            {userData?.fullName?.charAt(0)?.toUpperCase() || 'S'}
          </button>

          <button className="ios-logout" type="button" onClick={handleLogout}>
            Keluar
          </button>
        </div>
      </header>

      <main className="ios-content">
        {/* ===== HOME ===== */}
        {activeTab === 'home' && (
          <>
            <section className="ios-hero">
              <div className="ios-hero-bg" />
              <div className="ios-hero-card">
                <div className="ios-hero-text">
                  <div className="ios-hero-kicker">{getGreeting()}</div>
                  <h2 className="ios-hero-title">
                    Hai, {firstName}
                  </h2>
                  <p className="ios-hero-desc">Semak jadual, kehadiran dan mood harian awak dalam satu tempat.</p>

                  <div className="ios-pillrow">
                    <span className="ios-pill">Sekolah {userData?.schoolName || 'Sekolah'}</span>
                    <span className="ios-pill">Daerah {userData?.daerah || '-'}</span>
                    <span className="ios-pill">Terminal {userData?.terminal || '-'}</span>
                  </div>
                </div>

                <div className="ios-hero-mini">
                  <div className={`ios-mini-card attendance-${attendanceTone}`}>
                    <div className="ios-mini-label">Kehadiran</div>
                    <div className="ios-mini-value">
                      {attendanceLoading ? '...' : (attendanceRate === null ? '-' : `${attendanceRate}%`)}
                    </div>
                  </div>
                  <div className="ios-mini-card">
                    <div className="ios-mini-label">Streak hadir</div>
                    <div className="ios-mini-value">
                      {attendanceLoading ? '...' : `${currentStreak}`}
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <section className="ios-section">
              <div className="ios-section-head">
                <h3>Tindakan Pantas</h3>
              </div>

              <div className="ios-actions">
                <button className="ios-action ios-action-schedule" type="button" onClick={goSchedule}>
                  <div className="ios-action-icon ios-action-icon-schedule" aria-hidden="true"><span /></div>
                  <div className="ios-action-text">
                    <div className="ios-action-title">Jadual</div>
                    <div className="ios-action-sub">Lihat semua kelas</div>
                  </div>
                  <div className="ios-action-arrow">></div>
                </button>

                <button className="ios-action ios-action-mood" type="button" onClick={() => setActiveTab('mood')}>
                  <div className="ios-action-icon ios-action-icon-mood" aria-hidden="true"><span /></div>
                  <div className="ios-action-text">
                    <div className="ios-action-title">Mood Hari Ini</div>
                    <div className="ios-action-sub">
                      {moodLoading
                        ? '...'
                        : todayMood
                        ? `${todayMood.moodLabel} / ${moodCheckedInLabel || 'Masa belum direkod'}`
                        : `Belum isi hari ini / ${formatMalayDate(todayISO)}`}
                    </div>
                  </div>
                  <div className="ios-action-arrow">></div>
                </button>

                <button className="ios-action ios-action-progress" type="button" onClick={() => setActiveTab('performance')}>
                  <div className="ios-action-icon ios-action-icon-progress" aria-hidden="true"><span /></div>
                  <div className="ios-action-text">
                    <div className="ios-action-title">Prestasi</div>
                    <div className="ios-action-sub">
                      {performanceLoading ? 'Memuatkan...' : `${achievedSubjectCount}/4 subjek capai sasaran`}
                    </div>
                  </div>
                  <div className="ios-action-arrow">></div>
                </button>

                {generalSupportUrl && (
                  <a className="ios-action ios-action-support" href={generalSupportUrl} target="_blank" rel="noreferrer">
                    <div className="ios-action-icon ios-action-icon-support" aria-hidden="true"><span /></div>
                    <div className="ios-action-text">
                      <div className="ios-action-title">Sokongan</div>
                      <div className="ios-action-sub">Hubungi BTOP melalui WhatsApp</div>
                    </div>
                    <div className="ios-action-arrow">></div>
                  </a>
                )}
              </div>
            </section>

            {/* Stats */}
            <section className="ios-section">
              <div className="ios-section-head">
                <h3>Statistik Saya</h3>
              </div>

              <div className="ios-stats">
                <div className={`ios-stat ios-stat-blue attendance-${attendanceTone}`}>
                  <div className="ios-stat-top">
                    <span className="ios-stat-icon ios-stat-icon-attendance" aria-hidden="true"><span /></span>
                    <span className="ios-stat-chip">
                      {attendanceLoading ? 'Sedang selaras' : attendanceTone === 'good' ? 'Hijau' : attendanceTone === 'warn' ? 'Kuning' : attendanceTone === 'bad' ? 'Merah' : 'Belum lengkap'}
                    </span>
                  </div>
                  <div className="ios-stat-value">
                    {attendanceLoading ? '...' : (attendanceRate === null ? '-' : `${attendanceRate}%`)}
                  </div>
                  <div className="ios-stat-label">Kehadiran lepas</div>
                  <div className="ios-stat-sub">
                    {attendanceLoading ? '...' : `${attendedPastCount}/${pastSchedules.length || 0} sesi`}
                  </div>
                </div>

                <div className="ios-stat ios-stat-yellow">
                  <div className="ios-stat-top">
                    <span className="ios-stat-icon ios-stat-icon-mood" aria-hidden="true"><span /></span>
                    <span className="ios-stat-chip">
                      {moodLoading ? 'Sedang selaras' : 'Hari ini'}
                    </span>
                  </div>
                  <div className="ios-stat-value">
                    {moodLoading ? '...' : (todayMood ? todayMood.moodLabel : '-')}
                  </div>
                  <div className="ios-stat-label">Mood Hari Ini</div>
                  <div className="ios-stat-sub">
                    {moodLoading
                      ? '...'
                      : todayMood
                      ? `${todayMood.moodLabel}${moodCheckedInLabel ? ` / ${moodCheckedInLabel}` : ''}`
                      : 'Belum isi mood'}
                  </div>
                </div>

                <div className="ios-stat ios-stat-purple">
                  <div className="ios-stat-top">
                    <span className="ios-stat-icon ios-stat-icon-streak" aria-hidden="true"><span /></span>
                    <span className="ios-stat-chip">
                      {attendanceLoading ? 'Sedang selaras' : 'Aktif'}
                    </span>
                  </div>
                  <div className="ios-stat-value">{attendanceLoading ? '...' : currentStreak}</div>
                  <div className="ios-stat-label">Streak Kehadiran</div>
                  <div className="ios-stat-sub">hari berturut-turut</div>
                </div>

                <div className="ios-stat ios-stat-green">
                  <div className="ios-stat-top">
                    <span className="ios-stat-icon ios-stat-icon-schedule" aria-hidden="true"><span /></span>
                    <span className="ios-stat-chip">
                      {loadingSchedules ? 'Sedang selaras' : 'Sedia'}
                    </span>
                  </div>
                  <div className="ios-stat-value">{loadingSchedules ? '...' : schedules.length}</div>
                  <div className="ios-stat-label">Jadual tersedia</div>
                  <div className="ios-stat-sub">
                    {loadingSchedules ? '...' : `${upcoming.length} akan datang`}
                  </div>
                </div>
              </div>
            </section>

            {needsAttendanceFollowUp && attendanceSupportUrl && (
              <section className="ios-section">
                <div className={`student-support-card attendance-${attendanceTone}`}>
                  <div>
                    <span className="support-kicker">Sokongan kehadiran</span>
                    <h4>Kehadiran awak perlukan perhatian.</h4>
                    <p>
                      Kalau ada sebab tidak hadir atau rekod belum betul, awak boleh terus maklumkan kepada pihak BTOP.
                    </p>
                  </div>
                  <a className="support-whatsapp-btn" href={attendanceSupportUrl} target="_blank" rel="noreferrer">
                    WhatsApp BTOP
                  </a>
                </div>
              </section>
            )}

            {/* Upcoming */}
            <section className="ios-section">
              <div className="ios-section-head">
                <h3>Jadual Akan Datang</h3>
                <span className="ios-subpill">
                  {userData?.daerah || '-'} / {userData?.terminal || '-'}
                </span>
              </div>

              {loadingSchedules ? (
                <div className="ios-card ios-center">
                  <div className="ios-spinner small" />
                  <p>Memuatkan jadual...</p>
                </div>
              ) : upcoming.length === 0 ? (
                <div className="ios-card ios-empty">
                  <div className="ios-empty-emoji ios-empty-calendar" aria-hidden="true"><span /></div>
                  <h4>Belum ada jadual</h4>
                  <p>Belum ada jadual untuk terminal awak. Sila semak semula selepas pentadbir mengemaskini jadual.</p>
                  <button className="ios-cta" type="button" onClick={goSchedule}>
                    Buka Jadual Penuh
                  </button>
                </div>
              ) : (
                <div className="ios-list">
                  {upcoming.map((s) => {
                    const past = isPastSession(s.dateISO, s.startTime);

                    const typeLabel =
                      s.type === 'PlayAndTalk' ? 'Play & Talk' :
                      s.type === 'IVC' ? 'Kelas IVC' :
                      s.type === 'Workshop' ? 'Bengkel' :
                      s.type === 'Quiz' ? 'Kuiz' :
                      s.type === 'Seminar' ? 'Seminar' : 'Aktiviti';

                    return (
                      <div key={s.id} className={`ios-schedule ${past ? 'past' : ''}`}>
                        <div className="ios-schedule-left">
                          <div className="ios-time">{formatTime(s.startTime)}</div>
                          <div className="ios-time-sub">{s.endTime ? formatTime(s.endTime) : ''}</div>
                        </div>

                        <div className="ios-schedule-mid">
                          <div className="ios-schedule-topline">
                            <div className="ios-subject">{s.subject || 'Subjek'}</div>
                            <div className={`ios-status ${past ? 'done' : 'soon'}`}>
                              {past ? 'Selesai' : 'Akan datang'}
                            </div>
                          </div>

                          <div className="ios-meta">Tarikh {formatMalayDate(s.dateISO)}</div>
                          <div className="ios-meta">Guru {s.teacher || '-'}</div>
                          <div className="ios-meta">
                            Daerah {s.daerah || '-'} / {s.terminal || '-'} {s.room ? `/ Bilik ${s.room}` : ''}
                          </div>

                          <div className="ios-tag">{typeLabel}</div>

                        </div>
                      </div>
                    );
                  })}

                  {schedules.length > 5 && (
                    <button className="ios-list-more" type="button" onClick={goSchedule}>
                      Lihat Semua Jadual ({schedules.length})
                    </button>
                  )}
                </div>
              )}
            </section>

            <div className="ios-safe-bottom" />
          </>
        )}

        {/* ===== MOOD TAB ===== */}
        {activeTab === 'mood' && (
          <>
            <section className="ios-section">
              <div className="ios-section-head">
                <h3>Mood Hari Ini</h3>
                <span className="ios-subpill">{formatMalayDate(todayISO)}</span>
              </div>

              <div className="ios-card mood-card">
                <div className="mood-title">Macam mana perasaan awak hari ini?</div>

                <div className="mood-grid">
                  {moodOptions.map((m) => (
                    <button
                      key={m.key}
                      type="button"
                      className={`mood-chip ${moodKey === m.key ? 'active' : ''}`}
                      onClick={() => setMoodKey(m.key)}
                    >
                      <span className={`m-emo m-emo-${m.key}`} aria-hidden="true" />
                      <span className="m-label">{m.label}</span>
                    </button>
                  ))}
                </div>

                <div className="mood-note">
                  <label>Nota (pilihan)</label>
                  <textarea
                    value={moodNote}
                    onChange={(e) => setMoodNote(e.target.value)}
                    placeholder="Contoh: hari ni penat sikit sebab banyak kelas..."
                    rows={4}
                  />
                </div>

                <div className="mood-actions">
                  <button className="ios-btn ghost" type="button" onClick={() => setActiveTab('home')}>
                    Batal
                  </button>

                  <button className="ios-btn solid" type="button" onClick={handleSaveMood} disabled={savingMood}>
                    {savingMood ? 'Menyimpan...' : (todayMood ? 'Kemaskini Mood' : 'Simpan Mood')}
                  </button>
                </div>

                {todayMood && (
                  <div className="mood-hint">
                    <span>Mood hari ini:</span>{' '}
                    <strong>{todayMood.moodLabel}</strong>
                    {moodCheckedInLabel && (
                      <small>Direkod pada {moodCheckedInLabel}</small>
                    )}
                  </div>
                )}

                {moodSupportUrl && (
                  <div className="mood-support-box">
                    <div>
                      <strong>Adakah anda ingin bercakap dengan kami?</strong>
                      <span>Jika perlukan sokongan, tekan butang ini untuk terus ke WhatsApp BTOP.</span>
                    </div>
                    <a href={moodSupportUrl} target="_blank" rel="noreferrer">
                      WhatsApp BTOP
                    </a>
                  </div>
                )}
              </div>
            </section>

            <div className="ios-safe-bottom" />
          </>
        )}

        {/* ===== PERFORMANCE TAB ===== */}
        {activeTab === 'performance' && (
          <>
            <section className="ios-section">
              <div className="ios-section-head">
                <h3>Prestasi Akademik</h3>
                <span className="ios-subpill">Sasaran naik 2 gred</span>
              </div>

              <div className="ios-card performance-hero">
                <div>
                  <p>Fokus Program BEST</p>
                  <h2>{achievedSubjectCount}/4 subjek capai sasaran</h2>
                  <span>Gred permulaan dibandingkan dengan markah dan gred terkini untuk BM, BI, MATH dan SEJARAH.</span>
                </div>
                <div className="performance-ring">
                  <strong>{Math.round((achievedSubjectCount / 4) * 100)}%</strong>
                  <span>perkembangan</span>
                </div>
              </div>
              <div className="ios-attendance-legend">
                <span className="good">Hijau 90%+</span>
                <span className="warn">Kuning 75-89%</span>
                <span className="bad">Merah &lt;75%</span>
                <small>Warna ini bantu kesan status kehadiran dengan cepat.</small>
              </div>
            </section>

            <section className="ios-section">
              {performanceLoading ? (
                <div className="ios-card ios-center">
                  <div className="ios-spinner small" />
                  <p>Memuatkan prestasi...</p>
                </div>
              ) : (
                <div className="performance-grid">
                  {performanceBySubject.map((item) => (
                    <article key={item.subject} className={`performance-card ${item.achieved ? 'achieved' : ''}`}>
                      <div className="performance-card-head">
                        <div>
                          <span>Subjek</span>
                          <h4>{item.subject}</h4>
                        </div>
                        <strong className={item.achieved ? 'ok' : 'pending'}>
                          {item.achieved ? 'Capai sasaran' : item.hasRecord ? 'Dalam perkembangan' : 'Belum ada rekod'}
                        </strong>
                      </div>

                      <div className="grade-row">
                        <div>
                          <span>Baseline</span>
                          <strong>{item.baselineGrade}</strong>
                          <small>{item.baselineMark === null ? '-' : item.baselineMark} markah</small>
                        </div>
                        <div>
                          <span>Sasaran</span>
                          <strong>{item.targetGrade}</strong>
                          <small>+2 gred</small>
                        </div>
                        <div>
                          <span>Terkini</span>
                          <strong>{item.currentGrade}</strong>
                          <small>{item.currentMark === null ? '-' : item.currentMark} markah</small>
                        </div>
                      </div>

                      <div className="mark-bar">
                        <div style={{ width: `${item.markProgress}%` }} />
                      </div>

                      <div className="performance-foot">
                        <span>{item.examLabel || 'Belum ada rekod peperiksaan'}</span>
                        {item.markDelta === null ? (
                          <strong>Menunggu rekod pentadbir</strong>
                        ) : (
                          <strong className={item.markDelta >= 0 ? 'up' : 'down'}>
                            {item.markDelta > 0 ? `+${item.markDelta}` : item.markDelta} markah
                          </strong>
                        )}
                      </div>
                    </article>
                  ))}
                </div>
              )}
            </section>

            <div className="ios-safe-bottom" />
          </>
        )}

        {/* ===== PROFILE TAB ===== */}
        {activeTab === 'profile' && (
          <>
            <section className="ios-profile-hero">
              <div className="ios-profile-card">
                <div className="ios-profile-top">
                  <div className="ios-profile-avatar">
                    {userData?.fullName?.charAt(0)?.toUpperCase() || 'S'}
                  </div>
                  <div className="ios-profile-nameblock">
                    <div className="name">{safeText(userData?.fullName)}</div>
                    <div className="meta">{safeText(userData?.email)}</div>
                  </div>

                  <button
                    type="button"
                    className="ios-editbtn"
                    onClick={() => setEditMode((v) => !v)}
                  >
                    {editMode ? 'Tutup' : 'Edit'}
                  </button>
                </div>

                <div className="ios-profile-pills">
                  <span className="pill">Sekolah {safeText(userData?.schoolName)}</span>
                  <span className="pill">Daerah {safeText(userData?.daerah)}</span>
                  <span className="pill">Terminal {safeText(userData?.terminal)}</span>
                </div>

                <div className="ios-profile-minirow">
                  <div className="mini">
                    <div className="k">Daftar sejak</div>
                    <div className="v">{joinedLabel}</div>
                  </div>
                  <div className="mini">
                    <div className="k">Peranan</div>
                    <div className="v">{safeText(userData?.role)}</div>
                  </div>
                </div>

                {editMode && (
                  <div className="ios-profile-edit">
                    <div className="field">
                      <label>Nama Penuh</label>
                      <input
                        value={profileForm.fullName}
                        onChange={(e) => setProfileForm((p) => ({ ...p, fullName: e.target.value }))}
                        placeholder="Nama penuh"
                      />
                    </div>

                    <div className="field">
                      <label>No Telefon</label>
                      <input
                        value={profileForm.phone}
                        onChange={(e) => setProfileForm((p) => ({ ...p, phone: e.target.value }))}
                        placeholder="012-3456789"
                      />
                    </div>

                    <div className="field">
                      <label>Tingkatan</label>
                      <select
                        value={profileForm.grade}
                        onChange={(e) => setProfileForm((p) => ({ ...p, grade: e.target.value }))}
                      >
                        <option value="">-</option>
                        <option value="Tingkatan 1">Tingkatan 1</option>
                        <option value="Tingkatan 2">Tingkatan 2</option>
                        <option value="Tingkatan 3">Tingkatan 3</option>
                        <option value="Tingkatan 4">Tingkatan 4</option>
                        <option value="Tingkatan 5">Tingkatan 5</option>
                      </select>
                    </div>

                    <div className="edit-actions">
                      <button
                        className="ios-btn ghost"
                        type="button"
                        onClick={() => {
                          setEditMode(false);
                          setProfileForm({
                            fullName: userData?.fullName || '',
                            phone: userData?.phone || '',
                            grade: userData?.grade || '',
                          });
                        }}
                      >
                        Batal
                      </button>

                      <button
                        className="ios-btn solid"
                        type="button"
                        onClick={handleSaveProfile}
                        disabled={savingProfile}
                      >
                        {savingProfile ? 'Menyimpan...' : 'Simpan'}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </section>

            <section className="ios-section">
              <div className="ios-section-head">
                <h3>Akaun</h3>
              </div>

              <div className="ios-settings danger">
                <button className="ios-setting-item danger" type="button" onClick={handleLogout}>
                  <div className="l">
                    <span className="ico logout-ico" aria-hidden="true" />
                    <div>
                      <div className="t">Log keluar</div>
                      <div className="s">Keluar dari akaun pelajar</div>
                    </div>
                  </div>
                  <div className="r">></div>
                </button>
              </div>
            </section>

            <div className="ios-safe-bottom" />
          </>
        )}
      </main>

    </div>
    </DashboardShell>
  );
};

export default StudentDashboard;
